/**
 * Trial operational limits — frontend mirror of the backend caps.
 *
 * Source of truth: the `trial` tier in
 * `apps/backend/src/billing/billing-config.service.ts`
 * (`maxStudies`, `maxChatRooms`, `maxChatMessagesPerRoom`).
 *
 * Only the standard, no-credit-card `trial` tier carries these caps. Other
 * trial-shaped tiers (`partner_trial`, `vip_trial`, `pro_trial`) are unlimited
 * (`-1`) for these operations, so they intentionally do NOT match `isTrialTier`.
 *
 * The backend enforces all three caps and returns a `403` when a limit is hit,
 * so these constants drive proactive UI only — `detectTrialLimitError` keeps the
 * UI correct even if a client-side count is stale.
 */

import type { SubscriptionTier } from "@/lib/api";

export const TRIAL_MAX_CHAT_MESSAGES_PER_ROOM = 5;
export const TRIAL_MAX_CHAT_ROOMS = 3;
export const TRIAL_MAX_STUDIES = 1;

export type TrialLimitKind = "messages" | "rooms" | "studies";

/**
 * True only for the standard no-card `trial` tier — the single tier the backend
 * applies the 1/3/5 caps to. Mirror with `getPlanConfig('trial')` on the backend.
 */
export function isTrialTier(
  tier: SubscriptionTier | string | null | undefined,
): boolean {
  return tier === "trial";
}

interface AxiosLikeError {
  response?: {
    status?: number;
    data?: { message?: string | string[] };
  };
  message?: string;
}

function readErrorMessage(error: unknown): string {
  const err = error as AxiosLikeError;
  const raw = err?.response?.data?.message;
  if (Array.isArray(raw)) {
    return raw.join(" ");
  }
  if (typeof raw === "string") {
    return raw;
  }
  return err?.message ?? "";
}

/**
 * Recognizes the backend's plain-text `403` trial-limit messages so callers can
 * surface the matching upgrade UI instead of a generic error. Returns `null`
 * when the error is not a recognized trial-limit rejection.
 *
 * Matches the copy thrown by the backend controllers:
 *   - chat rooms:    "...reached your chat room limit..."
 *   - chat messages: "...reached the message limit for this chat room..."
 *   - studies:       "...reached your study limit..."
 */
export function detectTrialLimitError(error: unknown): TrialLimitKind | null {
  const err = error as AxiosLikeError;
  if (err?.response?.status !== 403) {
    return null;
  }
  const message = readErrorMessage(error).toLowerCase();
  if (message.includes("message limit")) {
    return "messages";
  }
  if (message.includes("chat room limit")) {
    return "rooms";
  }
  if (message.includes("study limit")) {
    return "studies";
  }
  return null;
}
